import React, { forwardRef, useCallback, useImperativeHandle, useMemo, memo, useState, useEffect, useRef } from 'react';
import { Alert, Platform, View, useWindowDimensions, StyleSheet } from 'react-native';
import { debounce } from 'lodash';
import Svg, { Path, Circle, Line } from 'react-native-svg';
import { AntDesign } from '@expo/vector-icons';

const VIEW_BOX = 4000; // Match userHierarchy viewport

export interface UserLinkHandlerHandle {
  setValues: (values: any) => void;
  forceUpdate: () => void;
  // Removed updatePositions - no longer needed since we read from ref directly
}

export type UserLinkHandlerProps = {
  links: any[];
  layout?: { height: number; width: number };
  staticLink?: boolean;
  avatarPressCallback?: (id: string) => void;
  transformFactor?: number;
  linkDeleteCallback?: (i: number) => void;
  userPositionsRef: React.MutableRefObject<Record<string, { x: number, y: number }>>;  // Direct ref access
  theme?: boolean;
  zoomLevel?: number;
};

const UserLinkHandler = forwardRef<UserLinkHandlerHandle, UserLinkHandlerProps>(({
  links,
  transformFactor = 1,
  layout = { width: 0, height: 0 },
  staticLink,
  linkDeleteCallback,
  avatarPressCallback,
  userPositionsRef,  // Direct ref to positions
  theme = true,
  zoomLevel = 1
}, ref) => {
  const { width, height } = useWindowDimensions();
  const componentMountedRef = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Internal update trigger

  console.log('🔗 LinkHandler render:', {
    linksCount: links.length,
    theme,
    zoomLevel,
    hasPositionsRef: !!userPositionsRef,
    updateTrigger,
    renderTime: Date.now()
  });

  // Debounced update function to prevent excessive re-renders during dragging
  const debouncedUpdate = useMemo(
    () => debounce(() => {
      if (componentMountedRef.current) {
        setUpdateTrigger(prev => prev + 1);
      }
    }, 16), // 60fps update rate (16ms)
    []
  );

  // Handle imperative updates from parent component
  useImperativeHandle(ref, () => ({
    setValues(values: any) {
      console.log('🎯 LinkHandler setValues (legacy):', values);
      // Legacy method - not needed since we read from ref directly
    },
    forceUpdate() {
      debouncedUpdate();
    }
  }));

  // No position sync effects needed - we read directly from ref

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const _handleDeleteLink = useCallback((linkId: number) => {
    Alert.alert('Delete Link', 'Are you sure you want to delete this link?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', onPress: async () =>
          linkDeleteCallback?.(linkId)
      }
    ]);
  }, [linkDeleteCallback]);

  // Get line color based on connection type and theme
  const getLineColor = (connectionType?: string) => {
    if (!connectionType) return theme ? '#9E9E9E' : '#BDBDBD';

    switch (connectionType) {
      case 'reports_to': return theme ? '#4CAF50' : '#66BB6A';
      case 'manages': return theme ? '#2196F3' : '#42A5F5';
      case 'collaborates': return theme ? '#FF9800' : '#FFA726';
      default: return theme ? '#9E9E9E' : '#BDBDBD';
    }
  };

  // Component cleanup
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  if (!links?.length) return null;

  // Don't render until we have positions in the ref
  const currentPositions = userPositionsRef.current;
  const hasAnyPositions = Object.keys(currentPositions).length > 0;
  if (!hasAnyPositions) {
    console.log('🚫 LinkHandler: No positions available in ref yet, skipping render');
    return null;
  }

  return (
    <Svg
      style={{
        ...StyleSheet.absoluteFillObject,
        width: VIEW_BOX,
        height: VIEW_BOX,
        zIndex: 0 // Behind cards
      }}
    >
      {links.map((link, i) => {
        // Read positions directly from ref - always current!
        const source = staticLink ? link.sourceUser : currentPositions[link.sourceUser];
        const target = staticLink ? link.targetUser : currentPositions[link.targetUser];

        if (!source || !target) {
          // Only log missing positions once per link to reduce console noise
          const linkKey = `${link.sourceUser}-${link.targetUser}`;
          if (!componentMountedRef.current || !source || !target) {
            console.log('� Missing positions for link (will retry):', {
              linkId: i,
              linkKey,
              sourceId: link.sourceUser,
              targetId: link.targetUser,
              hasSource: !!source,
              hasTarget: !!target,
              availablePositions: Object.keys(currentPositions)
            });
          }
          return null;
        }

        // Calculate connection points (center of cards like userHierarchy)
        const sourceX = source.x + 100; // Center of card (200px width / 2)
        const sourceY = source.y + 60;  // Center of card (120px height / 2)
        const targetX = target.x + 100;
        const targetY = target.y + 60;

        // Create curved path like original but with modern styling
        const controlX1 = sourceX + (targetX - sourceX) / 2;
        const controlY1 = sourceY + 50;
        const controlX2 = targetX + (sourceX - targetX) / 2;
        const controlY2 = targetY - 50;

        const midX = (sourceX + targetX) / 2;
        const midY = (sourceY + targetY) / 2;

        const path = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${targetX} ${targetY}`;

        console.log('🔗 Rendering connection:', {
          linkId: i,
          source: { x: sourceX, y: sourceY, id: source.id },
          target: { x: targetX, y: targetY, id: target.id },
          type: link.type || 'default'
        });

        return (
          <React.Fragment key={`${link.sourceUser}-${link.targetUser}-${i}`}>
            {/* Connection line */}
            <Path
              d={path}
              fill="none"
              stroke={getLineColor(link.type)}
              strokeWidth={link.width || 2}
              opacity={0.8}
              strokeDasharray={link.type === 'collaborates' ? '5,5' : undefined}
            />

            {/* Delete button circle */}
            <Circle
              cx={midX}
              cy={midY}
              r={8}
              fill={theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'}
              stroke={theme ? '#FF5722' : '#FF8A65'}
              strokeWidth={2}
              onLongPress={() => _handleDeleteLink(i)}
              onPress={() => _handleDeleteLink(i)}
            />

            {/* Delete icon - using small circle instead of complex icon for better performance */}
            <Circle
              cx={midX}
              cy={midY}
              r={3}
              fill="#FF5722"
              pointerEvents="none"
            />
          </React.Fragment>
        );
      })}
    </Svg>
  );
});

UserLinkHandler.displayName = 'UserLinkHandler';

export default memo(UserLinkHandler, (prevProps, nextProps) => {
  // Only re-render if essential props change (not positions or updateTrigger - those are handled imperatively)
  const linksChanged = JSON.stringify(prevProps.links) !== JSON.stringify(nextProps.links);
  const themeChanged = prevProps.theme !== nextProps.theme;
  const zoomChanged = prevProps.zoomLevel !== nextProps.zoomLevel;
  const staticChanged = prevProps.staticLink !== nextProps.staticLink;
  const shouldRerender = linksChanged || themeChanged || zoomChanged || staticChanged;
  return !shouldRerender; // Return true to prevent re-render
});
